import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Download, FileSpreadsheet, Package, Layers, Info, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const CATEGORY_ICONS = {
  sedia: "🪑", poltrona: "🛋️", sgabello: "🪑", divano: "🛋️",
  tavolo: "🍽️", tavolino: "☕", letto: "🛏️", comodino: "🗄️",
  credenza: "🪞", libreria: "📚", armadio: "🚪", specchio: "🪞", lampada: "💡",
};

export default function CasaMadreConfigTemplates() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/configurator/categories");
        setCategories(r.data || []);
      } catch (e) {
        toast.error("Errore caricamento categorie");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const download = async (catKey, catLabel) => {
    try {
      const token = localStorage.getItem("tk_token");
      const r = await fetch(`${API}/configurator/template.xlsx?category=${catKey}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(r.statusText);
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `configuratore_${catKey}.xlsx`;
      link.click();
      toast.success(`Template "${catLabel}" scaricato`);
    } catch (e) {
      toast.error("Errore download: " + e.message);
    }
  };

  const downloadIndex = async () => {
    try {
      const token = localStorage.getItem("tk_token");
      const r = await fetch(`${API}/configurator/template-index.xlsx`,
        { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "configuratore_indice.xlsx";
      link.click();
      toast.success("Indice scaricato");
    } catch (e) { toast.error("Errore: " + e.message); }
  };

  const filtered = categories.filter((c) =>
    !q || c.label.toLowerCase().includes(q.toLowerCase()) || c.key.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <AdminLayout title="Template Configuratore">
      <div className="max-w-[1400px] mx-auto p-6 lg:p-10">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="overline text-brand mb-2">Configuratore prodotto · Template Excel</div>
            <h1 className="font-heading font-bold text-4xl tracking-tight">Scarica i template di prodotto</h1>
            <p className="text-muted-foreground mt-2 text-sm max-w-2xl">
              Un foglio Excel pronto per ogni tipologia, con schede dedicate per ogni asse del configuratore (tessuti, gambe, metalli, piano, base, ecc.).
              Il prezzo finale a carrello sarà sempre <strong>prezzo base + Σ sovrapprezzi delle opzioni scelte</strong>.
            </p>
          </div>
          <button onClick={downloadIndex} data-testid="download-index"
            className="h-10 px-4 rounded-sm border border-border text-sm hover:bg-accent inline-flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" /> Indice completo
          </button>
        </div>

        {/* Info card */}
        <div className="bg-accent/30 border border-border rounded-sm p-4 mb-6 flex items-start gap-3">
          <Info className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed">
            <strong>Come usare i template:</strong> ① Scarica il file della tipologia che ti serve · ② Compila la scheda <em>Prodotto</em> con sku, prezzo base, dimensioni · ③ Compila ogni scheda asse (Tessuti, Gambe…) con codice, nome, sovrapprezzo · ④ Carica le immagini in <em>Media Library</em> e incolla gli URL · ⑤ (in arrivo) Importa il file da <em>Prodotti → Importa configuratore</em>.
            <div className="mt-2 text-xs text-muted-foreground">Le righe di esempio sono indicate con sfondo crema — eliminale prima di caricare il file definitivo.</div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="search-categories" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca tipologia (sedia, divano, tavolo...)" className="pl-9 h-11 rounded-sm" />
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground py-16">Caricamento categorie…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted-foreground py-16">Nessuna categoria trovata</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="categories-grid">
            {filtered.map((c) => (
              <div key={c.key} className="bg-white border border-border rounded-sm p-5 hover:shadow-md hover:border-brand/40 transition-all group" data-testid={`cat-${c.key}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="text-3xl leading-none">{CATEGORY_ICONS[c.key] || "📦"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-lg leading-tight truncate">{c.label}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{c.key}</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mb-1 mt-3">Assi configuratore</div>
                <div className="flex flex-wrap gap-1 mb-4 min-h-[24px]">
                  {c.axes.map((a) => (
                    <span key={a.key} className="px-2 py-0.5 bg-accent/40 rounded-full text-[11px] font-medium">{a.label}</span>
                  ))}
                </div>
                <button onClick={() => download(c.key, c.label)} data-testid={`download-${c.key}`}
                  className="w-full h-10 rounded-sm bg-foreground text-white text-sm font-medium hover:bg-brand transition-colors inline-flex items-center justify-center gap-2 group-hover:bg-brand">
                  <Download className="w-4 h-4" /> Scarica XLSX
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Formula reminder */}
        <div className="mt-10 bg-foreground text-white rounded-sm p-6">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-5 h-5" style={{ color: "#C6A664" }} />
            <div className="overline" style={{ color: "#C6A664" }}>Formula prezzo additivo</div>
          </div>
          <div className="font-mono text-sm md:text-base bg-black/30 rounded-sm px-4 py-3 mb-3">
            prezzo_finale &nbsp;=&nbsp; prezzo_base &nbsp;+&nbsp; Σ ( sovrapprezzo di ogni opzione scelta )
          </div>
          <div className="text-sm text-white/70 leading-relaxed">
            Esempio Varel Chair: <span className="font-mono">35,00 € (base) + 2,10 € (tessuto RUNO LVL3) + 5,00 € (gamba HORECA_GOLD_KD) + 3,00 € (metallo GOLD) = <strong className="text-white">45,10 €</strong></span>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
